import { useState, useEffect, useCallback } from 'react'
import { toast } from 'sonner'
import { api, type ExpenseCategory, type ExpenseGroupType } from '@/api/client'

const GROUP_LABELS: Record<ExpenseGroupType, string> = {
  MedicalCare: 'Medical Care',
  Medications: 'Medications',
  DentalVision: 'Dental & Vision',
  SuppliesEquipment: 'Supplies & Equipment',
  InsurancePremiums: 'Insurance Premiums',
  TransportationLodging: 'Transportation & Lodging',
  Other: 'Other',
}

const GROUP_COLORS: Record<ExpenseGroupType, string> = {
  MedicalCare: 'bg-blue-100 text-blue-800',
  Medications: 'bg-purple-100 text-purple-800',
  DentalVision: 'bg-teal-100 text-teal-800',
  SuppliesEquipment: 'bg-orange-100 text-orange-800',
  InsurancePremiums: 'bg-indigo-100 text-indigo-800',
  TransportationLodging: 'bg-yellow-100 text-yellow-800',
  Other: 'bg-gray-100 text-gray-800',
}

const ALL_GROUPS: ExpenseGroupType[] = [
  'MedicalCare',
  'Medications',
  'DentalVision',
  'SuppliesEquipment',
  'InsurancePremiums',
  'TransportationLodging',
  'Other',
]

interface CategoryFormData {
  name: string
  description: string
  group: ExpenseGroupType
  requiresLMN: boolean
}

const emptyForm: CategoryFormData = {
  name: '',
  description: '',
  group: 'MedicalCare',
  requiresLMN: false,
}

export default function CategoriesPage() {
  const [categories, setCategories] = useState<ExpenseCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [editingCategory, setEditingCategory] = useState<ExpenseCategory | null>(null)
  const [formData, setFormData] = useState<CategoryFormData>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true)
      const data = await api.getExpenseCategories()
      if (data) setCategories(data)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load categories')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchCategories()
  }, [fetchCategories])

  const openAddModal = () => {
    setEditingCategory(null)
    setFormData(emptyForm)
    setShowModal(true)
  }

  const openEditModal = (category: ExpenseCategory) => {
    setEditingCategory(category)
    setFormData({
      name: category.name,
      description: category.description || '',
      group: category.group,
      requiresLMN: category.requiresLMN,
    })
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingCategory(null)
    setFormData(emptyForm)
  }

  const handleSave = async () => {
    if (!formData.name.trim()) return

    setSaving(true)
    try {
      if (editingCategory) {
        await api.updateExpenseCategory(editingCategory.id, {
          name: formData.name,
          description: formData.description || undefined,
          group: formData.group,
          requiresLMN: formData.requiresLMN,
        })
      } else {
        await api.createExpenseCategory({
          name: formData.name,
          description: formData.description || undefined,
          group: formData.group,
          requiresLMN: formData.requiresLMN,
        })
      }
      closeModal()
      fetchCategories()
      toast.success(editingCategory ? 'Category updated' : 'Category created')
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save category')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await api.deleteExpenseCategory(id)
      setDeleteConfirm(null)
      fetchCategories()
      toast.success('Category deleted')
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete category')
      setDeleteConfirm(null)
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1B365D]">Expense Categories</h1>
          <p className="text-[#718096] mt-1">
            Manage the expense categories available to all users.
          </p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          + Add Category
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
          <button className="ml-2 underline" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      <div className="card">
        {loading ? (
          <div className="text-center py-8 text-[#718096]">Loading categories...</div>
        ) : categories.length === 0 ? (
          <div className="text-center py-8 text-[#718096]">
            <p>No expense categories defined yet.</p>
            <p className="text-sm mt-1">Add categories so users can classify their expenses.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {categories.map((category) => (
              <div
                key={category.id}
                className="border border-[#E2E8F0] rounded-lg p-4 hover:border-[#CBD5E0] transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-[#1B365D]">{category.name}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${GROUP_COLORS[category.group]}`}>
                        {GROUP_LABELS[category.group]}
                      </span>
                      {category.requiresLMN && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          LMN Required
                        </span>
                      )}
                    </div>

                    {category.description && (
                      <p className="text-sm text-[#718096] mt-1">{category.description}</p>
                    )}
                  </div>

                  <div className="flex gap-2 ml-4">
                    <button
                      className="text-sm text-[#1B365D] hover:underline"
                      onClick={() => openEditModal(category)}
                    >
                      Edit
                    </button>
                    {deleteConfirm === category.id ? (
                      <div className="flex gap-1">
                        <button
                          className="text-sm text-red-600 hover:underline"
                          onClick={() => handleDelete(category.id)}
                        >
                          Confirm
                        </button>
                        <button
                          className="text-sm text-[#718096] hover:underline"
                          onClick={() => setDeleteConfirm(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        className="text-sm text-red-600 hover:underline"
                        onClick={() => setDeleteConfirm(category.id)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Category Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-[#1B365D] mb-4">
                {editingCategory ? 'Edit Category' : 'Add Category'}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="label">Name *</label>
                  <input
                    className="input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder='e.g., "Doctor Visit"'
                  />
                </div>

                <div>
                  <label className="label">Group</label>
                  <select
                    className="input"
                    value={formData.group}
                    onChange={(e) => setFormData({ ...formData, group: e.target.value as ExpenseGroupType })}
                  >
                    {ALL_GROUPS.map((group) => (
                      <option key={group} value={group}>
                        {GROUP_LABELS[group]}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="label">Description</label>
                  <textarea
                    className="input"
                    rows={2}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Optional description"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.requiresLMN}
                      onChange={(e) => setFormData({ ...formData, requiresLMN: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-[#1B365D] focus:ring-[#1B365D]"
                    />
                    <span className="text-sm text-[#4A5568]">
                      Requires Letter of Medical Necessity (LMN)
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button className="btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handleSave}
                  disabled={saving || !formData.name.trim()}
                >
                  {saving ? 'Saving...' : editingCategory ? 'Update Category' : 'Add Category'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
